import { ParseError, ParseResult } from 'papaparse'
import React, { type PropsWithChildren, type ReactNode } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { usePapaParse } from 'react-papaparse'
import { LinkButton } from '../../../components/Button/LinkButton'
import { BulkInviteVerificationResponse, CheckedCsvInviteUser, CsvInviteUser, useBulkInviteVerificationMutation } from '../../../store/auth/auth'
import { CustomerFreeSeatsResponse } from '../../../store/plan/plan'
import { AvailableSeatsInfo } from '../AvailableSeatsInfo'
import { getInviteRoleGroups } from '../InviteRoleGroups'

const _MAX_CSV_SIZE_IN_BYTES = 10485760 // 10485760 = 10 Mb
// Bulk invite never offers the RFG groups, not even to RFG accounts
const roleGroups = getInviteRoleGroups()

interface BulkInvitationModalBodyProps {
  accountId: string
  error: ReactNode
  freeSeatsData?: CustomerFreeSeatsResponse
  isFreeSeatsFetching: boolean
  isFreeSeatsFetchingError: boolean
  onCsvClientVerificationCompleted: (users: CsvInviteUser[], error: Error | null) => void
  onCsvServerVerificationCompleted: (users: CheckedCsvInviteUser[], errors: string[]) => void
  onCsvServerVerificationStarted: () => void
  onFileSelected: () => void
}

export const BulkInvitationModalBody = ({
  accountId,
  children,
  error,
  freeSeatsData,
  isFreeSeatsFetching,
  isFreeSeatsFetchingError,
  onCsvClientVerificationCompleted,
  onCsvServerVerificationCompleted,
  onCsvServerVerificationStarted,
  onFileSelected,
}: PropsWithChildren<BulkInvitationModalBodyProps>) => {
  const [bulkInviteVerification] = useBulkInviteVerificationMutation()
  const { readString } = usePapaParse()

  const cleanupFileInput = (event: React.MouseEvent<HTMLInputElement>) => {
    if (event.currentTarget) {
      event.currentTarget.value = ''
    }
  }

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    onFileSelected()
    const file = event.target.files ? event.target.files[0] : null
    if (!file) {
      onCsvClientVerificationCompleted([], new Error('File is missing'))
      return
    }
    if (file.size > _MAX_CSV_SIZE_IN_BYTES) {
      onCsvClientVerificationCompleted([], new Error(`CSV file is too large, allowed size: ${(_MAX_CSV_SIZE_IN_BYTES / 1048576).toFixed(2)} MB`))
      return
    }

    const content = await file.text()
    readString<CsvInviteUser>(content, {
      header: true,
      complete: async (result: ParseResult<CsvInviteUser>) => {
        try {
          if (result.errors && result.errors.length > 0) {
            onCsvClientVerificationCompleted([], new Error(`Invalid CSV: ${result.errors.map((error: ParseError) => error.message).join(', ')}`))
            return
          }
          const users = result.data.map((user) => ({
            ...user,
            roleIds: roleGroups.find((roleGroup) => roleGroup.name === user.role)?.roleIds ?? [],
          }))
          onCsvClientVerificationCompleted(users, null)
          onCsvServerVerificationStarted()
          const verificationResult = (await bulkInviteVerification({ accountId, users }).unwrap()) as BulkInviteVerificationResponse
          onCsvServerVerificationCompleted(verificationResult.users, verificationResult.generalErrors)
        } catch (error) {
          onCsvClientVerificationCompleted([], error instanceof Error ? error : new Error('Unexpected error occurred'))
        }
      },
    })
  }

  const hasNoAvailableSeat = freeSeatsData ? freeSeatsData.seats.general.available === 0 && freeSeatsData.seats.sidecar.available === 0 : true

  return (
    <Container>
      <Row className='pb-3'>
        <Col>
          <AvailableSeatsInfo seats={freeSeatsData?.seats} isFetching={isFreeSeatsFetching} isError={isFreeSeatsFetchingError} />
        </Col>
      </Row>
      <Row>
        <Col>
          {!isFreeSeatsFetching && hasNoAvailableSeat ? (
            <LinkButton variant='primary' className='d-inline-flex' to='/account/plan'>
              Buy more
            </LinkButton>
          ) : (
            <>
              <label htmlFor='file' className='label-medium pb-2'>
                List of users to be invited. Download <a href='/dashboard/assets/sample-bulk-invite.csv'>sample CSV</a>.
              </label>
              <Form.Control id='file' type='file' onClick={cleanupFileInput} onChange={handleImport} accept='text/csv' />
            </>
          )}
        </Col>
      </Row>
      <Row>
        <Col>{error}</Col>
      </Row>
      <Row>
        <Col>{children}</Col>
      </Row>
    </Container>
  )
}
